import * as React from 'react';
import { inject, observer } from 'mobx-react';
import './coachingSessionListPane.module.scss';
import { CalendarView } from '../../molecules/calendar/CalendarView';
import { DateGroup } from '../../molecules/reservable/DateGroup';
import { ScheduleBadge, ScheduleListItem } from '../../molecules/reservable/ScheduleListItem';
import { groupByDate, ReservableType } from '../../../../commonv3/types/reservable';
import { BackButton } from '../../../../commonv3/components/atoms/buttons/BackButton';
import moment from 'moment-timezone';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import {
    isInitialCoachingSession,
    isRegularCoachingSession,
    SessionType,
    SessionTemplateKey,
} from '../../../types/sessions';
import { HomePageState } from '../../../containers/home/states/HomePageState';
import { InitialCoachingNotice } from '../../molecules/reservable/InitialCoachingNotice';
import { observable } from 'mobx';
import { LoaderRow } from '../../atoms/layout/LoaderRow';
import { getLocationSelector, getOnlineSelector, LocationSelector } from '../../molecules/forms/LocationSelector';
import { CheckBoxCardNoBorder } from '../../atoms/select/CheckBoxCardNoBorder';
import { WaitlistToggleButton } from './SessionListPane';
import { SessionWaitlistState } from './states/SessionWaitlistState';
import { SessionWaitlistDescription } from '../../molecules/session_waitlists/SessionWaitlistDescription';
import { InitialCoachingV2Notice } from '../../molecules/reservable/InitialCoachingV2Notice';

type Props = {
    homeState: HomePageState;
};

const MaskMessage = ({ templateKey }: { templateKey: SessionTemplateKey }): React.ReactElement => {
    switch (templateKey) {
        case SessionTemplateKey.ONLINE_INITIAL_GROUP_COACHING_V2_SECOND:
        case SessionTemplateKey.OFFLINE_INITIAL_GROUP_COACHING_V2_SECOND:
            return (
                <span styleName="pink">
                    初回コーチングSTEP2「ゴール設定編」は<br />
                    STEP1「価値観ワーク編」を完了すると<br />予約することができます
                </span>
            );
        case SessionTemplateKey.ONLINE_REGULAR_GROUP_COACHING:
        case SessionTemplateKey.OFFLINE_REGULAR_GROUP_COACHING:
            return (
                <span>
                    通常コーチングは初回コーチングを完了すると<br />予約することができます
                </span>
            );
        default:
            return null;
    }
};

@inject('homeState')
@observer
export class CoachingSessionListPane extends React.Component<Props> {
    @observable onlyInitialCoaching = true;
    @observable onlyRegularCoaching = true;
    @observable sessionWaitlistState = new SessionWaitlistState();

    async componentDidMount() {
        const { sessionList } = this.props.homeState;
        const { homeLocation } = GlobalStatesV3.currentUser;
        await homeLocation.reload();
        const { isHomeLocationSet, isCurrentLocationOnline, locationId } = homeLocation;
        sessionList.setSessionType(SessionType.GROUP_COACHING);

        await this.props.homeState.reloadLocations();
        await this.sessionWaitlistState.reloadSessionWaitlists();
        sessionList.setSelectedLocationSelectors(
            isHomeLocationSet && !isCurrentLocationOnline
                ? [getLocationSelector(locationId), getOnlineSelector()]
                : [getOnlineSelector()]
        );
    }

    render() {
        const { locations, reservationList, sessionList } = this.props.homeState;
        const {
            sessionsOfType,
            filteredSessions,
            selectedLocationSelectors,
            setSelectedLocationSelectors,
            selectedDate,
            setSelectedDate,
            setSelectedMonth,
            sessionListLoading,
        } = sessionList;
        const { isInitialCoachingVisible, reservableCoachingTypes, visibleCoachingTypes } = reservationList;
        const extraFilter = (s) => {
            if (!visibleCoachingTypes.includes(s.template_key)) {
                return false;
            }
            const isInitialSession = this.onlyInitialCoaching && isInitialCoachingSession(s.template_key);
            const isRegularSession = this.onlyRegularCoaching && isRegularCoachingSession(s.template_key);
            return isInitialSession || isRegularSession;
        };
        const groups = groupByDate(filteredSessions.filter(extraFilter), (r) => r.starts_at);
        const { availableTokens, loading: currentUserLoading } = GlobalStatesV3.currentUser;
        const { homeLocation } = GlobalStatesV3.currentUser;
        const { isHomeLocationSet, locationId: homeLocationId } = homeLocation;
        const loading = sessionListLoading || currentUserLoading || reservationList.loading || homeLocation.loading;

        return (
            <div id="coaching-session-list-pane" styleName="pane">
                {GlobalStatesV3.viewport.isMobile && <BackButton />}

                <div styleName="pane-header">
                    <div styleName="pane-title">コーチングの予約</div>
                    <div styleName="pane-header-border" />
                    <SessionWaitlistDescription />
                </div>
                <div styleName="views">
                    <div styleName="calendar-wrapper">
                        <CalendarView
                            startDate={moment().date(1)}
                            endDate={moment()
                                .add(6, 'month')
                                .date(1)}
                            selectedDate={selectedDate}
                            clickables={sessionsOfType.filter(extraFilter).map((r) => r.starts_at)}
                            highlights={sessionsOfType
                                .filter(extraFilter)
                                .filter((s) => isInitialCoachingSession(s.template_key))
                                .map((r) => moment(r.starts_at).startOf('day'))}
                            onClick={setSelectedDate}
                            onMonthChange={setSelectedMonth}
                        />
                    </div>
                    <div styleName="schedule-list-wrapper">
                        {isInitialCoachingVisible && <InitialCoachingNotice />}
                        <InitialCoachingV2Notice />
                        <div id="coaching-schedule-list-header" styleName="schedule-list-header">
                            <div styleName="schedule-list-header-box">
                                <div styleName="schedule-list-header-text">表示する拠点</div>
                                <LocationSelector
                                    locations={locations.getOrElse([])}
                                    homeLocationId={homeLocationId}
                                    isHomeLocationSet={isHomeLocationSet}
                                    showOnline={true}
                                    showOnlineFirst={true}
                                    showOther={false}
                                    locationSelectors={selectedLocationSelectors}
                                    onChange={setSelectedLocationSelectors}
                                />
                            </div>
                            {isInitialCoachingVisible && (
                                <div styleName="schedule-list-header-box" id="coaching-type-selector">
                                    <div styleName="schedule-list-header-text">種類</div>
                                    <div styleName="initial-filter">
                                        <CheckBoxCardNoBorder
                                            checked={this.onlyInitialCoaching}
                                            label="初回コーチング"
                                            onCheck={(checked) => (this.onlyInitialCoaching = checked)}
                                        />
                                        <CheckBoxCardNoBorder
                                            checked={this.onlyRegularCoaching}
                                            label="通常コーチング"
                                            onCheck={(checked) => (this.onlyRegularCoaching = checked)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div styleName="schedule-list-body">
                            {sessionListLoading && <LoaderRow />}
                            {groups.map((group) => (
                                <DateGroup key={group.date} date={group.date}>
                                    {group.entries.map((entry) => {
                                        const reserved = reservationList.isReserved(ReservableType.SESSION, entry.id);
                                        const full = entry.active_reservation_count >= entry.capacity;
                                        const initialCoaching = isInitialCoachingSession(entry.template_key);
                                        const isReservable = availableTokens.isAffordable(
                                            entry.required_token_type,
                                            entry.required_token_amount,
                                            entry.starts_at
                                        );

                                        return (
                                            <ScheduleListItem
                                                key={entry.id}
                                                title={entry.template_name}
                                                reservable={entry}
                                                actionItem={
                                                    <div styleName="action-items">
                                                        <Button
                                                            id="session-reserve-button"
                                                            styleName="button"
                                                            type="primary"
                                                            loading={currentUserLoading}
                                                            disabled={
                                                                !reservableCoachingTypes.includes(entry.template_key) ||
                                                                (!isReservable || reserved || full)
                                                            }
                                                            size={GlobalStatesV3.viewport.isMobile ? 'm' : 'xs'}
                                                            onClick={() =>
                                                                GlobalStatesV3.routing.push(
                                                                    V3Routes.sessionReservation(entry.id)
                                                                )
                                                            }
                                                        >
                                                            {reserved ? '予約済' : full ? '満席' : '選択する'}
                                                        </Button>
                                                        {isReservable && (
                                                            <WaitlistToggleButton
                                                                entry={entry}
                                                                reserved={reserved}
                                                                sessionWaitlistState={this.sessionWaitlistState}
                                                            />
                                                        )}
                                                    </div>
                                                }
                                                titleBadge={
                                                    initialCoaching ? (
                                                        <ScheduleBadge color="pink">初回</ScheduleBadge>
                                                    ) : null
                                                }
                                                masked={!reservableCoachingTypes.includes(entry.template_key)}
                                                maskMessage={
                                                    !reservableCoachingTypes.includes(entry.template_key) && (
                                                        <MaskMessage templateKey={entry.template_key} />
                                                    )
                                                }
                                            />
                                        );
                                    })}
                                </DateGroup>
                            ))}
                        </div>
                    </div>
                </div>
                {loading && <div id="loading-indicator" />}
            </div>
        );
    }
}
